/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ClipboardList, PlusCircle, Search, Edit2, Trash2,
  Filter, X, Tag, Activity, Building2, Layers,
  ChevronLeft, ChevronRight, Link2, UserCheck
} from 'lucide-react';
import { serviceRequestService } from '../services/serviceRequestService';
import { accountService } from '../services/accountService';
import { subAccountService } from '../services/subAccountService';
import { userService } from '../services/userService';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import DateRangePicker from '../components/DateRangePicker';
import toastService from '../utils/toastService';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import useServiceRequestTypes from '../hooks/useServiceRequestTypes';
import {
  SERVICE_REQUEST_STATUSES,
  SERVICE_REQUEST_STATUS_LABELS,
  UNASSIGNED_FILTER_VALUE,
} from '../constants/serviceRequests';
import {
  DEFAULT_ASSET_IDENTIFIER_LABEL,
  DEFAULT_EQUIPMENT_IDENTIFIER_LABEL,
  DEFAULT_REQUEST_NUMBER_LABEL,
  DEFAULT_CUSTOMER_NAME_LABEL,
  resolveAssetIdentifierLabel,
  resolveCustomerNameLabel,
  resolveEquipmentIdentifierLabel,
  resolveRequestNumberLabel,
  resolveTypeName,
  dedupeTypesByCode,
} from '../utils/serviceRequestLabels';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 10;

const INITIAL_FILTERS = {
  search: '',
  type: 'All',
  status: 'All',
  createdFrom: '',
  createdTo: '',
  accountUid: '',
  subAccountUid: '',
  assignedUserUid: '',
};

// Case-insensitive A→Z sort of filter options by the given label field.
const sortByLabel = (list, field = 'name') =>
  [...list].sort((a, b) => (a[field] || '').localeCompare(b[field] || '', undefined, { sensitivity: 'base' }));

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const ServiceRequests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const { user } = useAuth();

  const initialFilters = location.state?.status
    ? { ...INITIAL_FILTERS, status: location.state.status }
    : INITIAL_FILTERS;

  const [serviceRequests, setServiceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState(initialFilters);
  const [debouncedFilters, setDebouncedFilters] = useState(initialFilters);

  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const showAccountColumn = !user?.accountUID;
  const showSubAccountColumn = !user?.subAccountUID;
  const showAccountFilter = !user?.accountUID;
  const canViewSubAccounts = can.sub_accounts.view();
  const showSubAccountFilter = !user?.subAccountUID && canViewSubAccounts;
  // Without users.view the assignee list can't be loaded, so the
  // assignee filter is hidden; the listing still shows every scoped request.
  const canViewUsers = can.users.view();
  const effectiveAccountId = filters.accountUid || user?.accountUID || '';
  const effectiveSubAccountId = filters.subAccountUid || user?.subAccountUID || '';

  // Dynamic layout column spans based on user scope and available filter count
  const filterColSpans = useMemo(() => {
    if (showAccountFilter && showSubAccountFilter) {
      // 6 filters (Super Admin)
      // Widescreen (xl/2xl): 1 row (2+2+2+2+2+2 = 12 cols, or 3+2+2+1+1+3 on 2xl)
      // Desktop (lg): 2 balanced rows of 3 (4 cols each)
      // Tablet (sm): 3 balanced rows of 2 (6 cols each)
      return {
        search: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-3',
        account: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-2',
        subAccount: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-2',
        type: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-1',
        status: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-1',
        assignedUser: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-2',
        dateRange: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2 2xl:col-span-3',
      };
    }
    if (!showAccountFilter && showSubAccountFilter) {
      // 5 filters (Account Admin)
      // Widescreen (xl): 1 row (3+2+2+2+3 = 12 cols)
      // Desktop (lg): Row 1 (4+4+4 = 12 cols, 3 items), Row 2 (6+6 = 12 cols, 2 items)
      // Tablet (sm): Row 1 (6+6 = 12 cols, 2 items), Row 2 (4+4+4 = 12 cols, 3 items)
      return {
        search: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-3',
        subAccount: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2',
        type: 'col-span-12 sm:col-span-4 lg:col-span-4 xl:col-span-2',
        status: 'col-span-12 sm:col-span-4 lg:col-span-6 xl:col-span-2',
        assignedUser: 'col-span-12 sm:col-span-4 lg:col-span-6 xl:col-span-2',
        dateRange: 'col-span-12 sm:col-span-4 lg:col-span-6 xl:col-span-3',
      };
    }
    if (showAccountFilter && !showSubAccountFilter) {
      // 5 filters (Super Admin without sub_accounts.view) — same spans as Account Admin
      return {
        search: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-3',
        account: 'col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2',
        type: 'col-span-12 sm:col-span-4 lg:col-span-4 xl:col-span-2',
        status: 'col-span-12 sm:col-span-4 lg:col-span-6 xl:col-span-2',
        assignedUser: 'col-span-12 sm:col-span-4 lg:col-span-6 xl:col-span-2',
        dateRange: 'col-span-12 sm:col-span-4 lg:col-span-6 xl:col-span-3',
      };
    }
    // 4 filters (Sub-Account User / Service User)
    // Desktop (lg/xl): 1 row (4+2+2+4 = 12 cols)
    // Tablet (sm): Row 1 (Search 6, Type 3, Status 3 = 12 cols), Row 2 (DateRange 12 cols)
    return {
      search: 'col-span-12 sm:col-span-6 lg:col-span-4',
      type: 'col-span-12 sm:col-span-3 lg:col-span-2',
      status: 'col-span-12 sm:col-span-3 lg:col-span-2',
      assignedUser: 'col-span-12 sm:col-span-6 lg:col-span-2',
      dateRange: 'col-span-12 sm:col-span-12 lg:col-span-4',
    };
  }, [showAccountFilter, showSubAccountFilter]);

  useEffect(() => {
    if (!user?.accountUID) {
      const fetchAccounts = async () => {
        try {
          const { data } = await accountService.getAccounts({ limit: 100 });
          setAccounts(sortByLabel(data.data || data || []));
        } catch {
          toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
        }
      };
      fetchAccounts();
    }
  }, [user?.accountUID]);

  useEffect(() => {
    if (!showSubAccountFilter) { return; }
    const fetchSubAccounts = async () => {
      try {
        const { data } = await subAccountService.getSubAccounts(effectiveAccountId || null, { limit: 100 });
        setSubAccounts(sortByLabel(data.data || []));
      } catch {
        toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchSubAccounts();
  }, [effectiveAccountId, showSubAccountFilter]);

  useEffect(() => {
    setFilters((f) => (f.subAccountUid ? { ...f, subAccountUid: '', assignedUserUid: '' } : f));
  }, [filters.accountUid]);

  useEffect(() => {
    setFilters((f) => (f.assignedUserUid ? { ...f, assignedUserUid: '' } : f));
    if (!canViewUsers || !effectiveSubAccountId) { setAssignableUsers([]); return; }
    const fetchAssignableUsers = async () => {
      try {
        const { data } = await userService.getUsers({ subAccountUid: effectiveSubAccountId, status: 'active', limit: 100 });
        setAssignableUsers(sortByLabel(data.users || [], 'displayName'));
      } catch {
        toastService.error('Failed to load assignable users');
      }
    };
    fetchAssignableUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSubAccountId, canViewUsers]);

  useEffect(() => {
    if (location.state?.status) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state?.status, navigate]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { types } = useServiceRequestTypes(filters.subAccountUid || undefined, {
    requireSubAccountUid: false,
    includeInactive: true,
  });

  // Headers follow one type's labels only when the grid is unambiguous:
  // an explicit Type filter, or exactly one type available in scope
  // (inactive included, since older rows can still carry one).
  const uniqueTypes = useMemo(() => dedupeTypesByCode(types), [types]);
  const labelTypeCode = filters.type !== 'All'
    ? filters.type
    : (uniqueTypes.length === 1 ? uniqueTypes[0].code : null);

  const assetIdentifierLabel = labelTypeCode
    ? resolveAssetIdentifierLabel(labelTypeCode, types)
    : DEFAULT_ASSET_IDENTIFIER_LABEL;
  const equipmentIdentifierLabel = labelTypeCode
    ? resolveEquipmentIdentifierLabel(labelTypeCode, types)
    : DEFAULT_EQUIPMENT_IDENTIFIER_LABEL;
  const requestNumberLabel = labelTypeCode
    ? resolveRequestNumberLabel(labelTypeCode, types)
    : DEFAULT_REQUEST_NUMBER_LABEL;
  const customerNameLabel = labelTypeCode
    ? resolveCustomerNameLabel(labelTypeCode, types)
    : DEFAULT_CUSTOMER_NAME_LABEL;

  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => setDebouncedFilters(filters), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const requestIdRef = useRef(0);
  const fetchServiceRequests = async (pageOverride) => {
    const currentRequestId = ++requestIdRef.current;
    const currentPagination = paginationRef.current;
    try {
      setLoading(true);
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
        type: debouncedFilters.type === 'All' ? undefined : debouncedFilters.type,
        status: debouncedFilters.status === 'All' ? undefined : debouncedFilters.status,
        search: debouncedFilters.search || undefined,
        createdFrom: debouncedFilters.createdFrom || undefined,
        createdTo: debouncedFilters.createdTo || undefined,
        accountUid: debouncedFilters.accountUid || undefined,
        subAccountUid: debouncedFilters.subAccountUid || undefined,
        assignedUserUid: debouncedFilters.assignedUserUid || undefined,
      };

      const { data } = await serviceRequestService.getServiceRequests(params);
      if (currentRequestId !== requestIdRef.current) return;
      setServiceRequests(data.data || []);
      setPagination({
        page: data.page ?? currentPagination.page,
        limit: data.limit ?? currentPagination.limit,
        total: data.total ?? 0,
        totalPages: Math.ceil((data.total ?? 0) / (data.limit ?? currentPagination.limit)),
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      toastService.error(err.response?.data?.message || `Failed to load ${SERVICE_REQUEST_LABEL.pluralLower}`);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchServiceRequests(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchServiceRequests(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await serviceRequestService.deleteServiceRequest(deleteTarget.uid);
      toastService.success(`${SERVICE_REQUEST_LABEL.singular} deleted successfully`);
      setDeleteTarget(null);
      fetchServiceRequests(pagination.page);
    } catch (err) {
      toastService.error(err.response?.data?.message || `Failed to delete ${SERVICE_REQUEST_LABEL.singularLower}`);
    } finally {
      setDeleting(false);
    }
  };

  // Active filters calculation
  const hasActiveFilters = Boolean(
    filters.search ||
    filters.type !== 'All' ||
    filters.status !== 'All' ||
    filters.accountUid ||
    filters.subAccountUid ||
    filters.assignedUserUid ||
    filters.createdFrom ||
    filters.createdTo
  );

  const canManage = can.service_requests.edit() || can.service_requests.delete() || can.service_requests.create();
  // Copy-link is view-level (matches the Detail page, which shows the same
  // link to any service_requests.view caller), so the Actions column must
  // still render for a view-only user even without edit/delete/create.
  const showActionsColumn = canManage || can.service_requests.view();

  const handleCopyPublicLink = (sr) => {
    if (!sr.hasActivePublicLink || !sr.publicLinkCode) return;
    const url = `${window.location.origin}/s/${sr.publicLinkCode}`;
    navigator.clipboard.writeText(url);
    toastService.success('Public link copied to clipboard');
  };

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {SERVICE_REQUEST_LABEL.plural}
            </h1>
            <p className="text-xs text-base-content/50">
              Track, inspect, and manage {SERVICE_REQUEST_LABEL.pluralLower}, captured media, and activity.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Create New Request */}
            {can.service_requests.create() && (
              <button
                onClick={() => navigate('/service-requests/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New {SERVICE_REQUEST_LABEL.singular}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        {/* Main Search and Filter Grid */}
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={`relative ${filterColSpans.search}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by number, customer, asset, equipment..."
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Account Filter (if visible) */}
          {showAccountFilter && (
            <div className={filterColSpans.account}>
              <select
                className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={filters.accountUid}
                onChange={(e) => setFilters(f => ({ ...f, accountUid: e.target.value, subAccountUid: '' }))}
              >
                <option value="">All {ACCOUNT_LABEL.plural}</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Sub Account Filter (if visible) */}
          {showSubAccountFilter && (
            <div className={filterColSpans.subAccount}>
              <select
                className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={filters.subAccountUid}
                onChange={(e) => setFilters(f => ({ ...f, subAccountUid: e.target.value }))}
              >
                <option value="">All {SUB_ACCOUNT_LABEL.plural}</option>
                {subAccounts.map((sub) => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Assigned To Filter */}
          {canViewUsers && (
          <div className={filterColSpans.assignedUser}>
            <select
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.assignedUserUid}
              disabled={!effectiveSubAccountId}
              onChange={(e) => setFilters(f => ({ ...f, assignedUserUid: e.target.value }))}
            >
              <option value="">All Assignees</option>
              <option value={UNASSIGNED_FILTER_VALUE}>Unassigned</option>
              {assignableUsers.map((u) => (
                <option key={u.userId} value={u.userId}>{u.displayName}</option>
              ))}
            </select>
          </div>
          )}

          {/* Type Select */}
          <div className={filterColSpans.type}>
            <select
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.type}
              onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}
            >
              <option value="All">All Types</option>
              {/* Active types first, then inactive — each group A→Z */}
              {sortByLabel(dedupeTypesByCode(types))
                .sort((a, b) => Number(!a.isActive) - Number(!b.isActive))
                .map((t) => (
                <option key={t.code} value={t.code}>{t.name}{!t.isActive ? ' (Inactive)' : ''}</option>
              ))}
            </select>
          </div>

          {/* Status Select */}
          <div className={filterColSpans.status}>
            <select
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="All">All Statuses</option>
              {SERVICE_REQUEST_STATUSES.map((s) => (
                <option key={s} value={s}>{SERVICE_REQUEST_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {/* Date Range Picker */}
          <div className={filterColSpans.dateRange}>
            <DateRangePicker
              startDate={filters.createdFrom}
              endDate={filters.createdTo}
              onChange={({ startDate, endDate }) => {
                setFilters((f) => ({
                  ...f,
                  createdFrom: startDate,
                  createdTo: endDate,
                }));
              }}
              placeholder="Filter by created date range"
            />
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, search: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.type !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Tag size={10} className="text-primary" />
                  <span>Type: {resolveTypeName(filters.type, types)}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, type: 'All' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Activity size={10} className="text-primary" />
                  <span>Status: {SERVICE_REQUEST_STATUS_LABELS[filters.status] || filters.status}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, status: 'All' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.accountUid && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Building2 size={10} className="text-primary" />
                  <span>{ACCOUNT_LABEL.singular}: {accounts.find(a => a.id === filters.accountUid)?.name || filters.accountUid}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, accountUid: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.subAccountUid && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Layers size={10} className="text-primary" />
                  <span>{SUB_ACCOUNT_LABEL.singular}: {subAccounts.find(s => s.id === filters.subAccountUid)?.name || filters.subAccountUid}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, subAccountUid: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.assignedUserUid && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <UserCheck size={10} className="text-primary" />
                  <span>
                    Assigned To: {filters.assignedUserUid === UNASSIGNED_FILTER_VALUE
                      ? 'Unassigned'
                      : (assignableUsers.find(u => u.userId === filters.assignedUserUid)?.displayName || filters.assignedUserUid)}
                  </span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, assignedUserUid: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {(filters.createdFrom || filters.createdTo) && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Date: {filters.createdFrom || 'Start'} → {filters.createdTo || 'End'}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, createdFrom: '', createdTo: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : serviceRequests.length === 0 ? (
          <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
              <ClipboardList size={28} />
            </div>
            <div>
              <h3 className="text-base font-black text-base-content">
                {hasActiveFilters ? `No matching ${SERVICE_REQUEST_LABEL.pluralLower}` : `No ${SERVICE_REQUEST_LABEL.pluralLower} yet`}
              </h3>
              <p className="text-xs text-base-content/40 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search criteria, dates, or clearing active filters.'
                  : `Get started by creating your first ${SERVICE_REQUEST_LABEL.singularLower} record.`}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
              >
                Clear all filters
              </button>
            ) : can.service_requests.create() ? (
              <button
                type="button"
                onClick={() => navigate('/service-requests/new')}
                className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
              >
                <PlusCircle size={14} />
                <span>Create {SERVICE_REQUEST_LABEL.singular}</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  <th className="py-3.5 px-5">{requestNumberLabel}</th>
                  <th className="py-3.5 px-4">Type</th>
                  <th className="py-3.5 px-4">{customerNameLabel}</th>
                  {showAccountColumn && <th className="py-3.5 px-4">{ACCOUNT_LABEL.singular}</th>}
                  {showSubAccountColumn && <th className="py-3.5 px-4">{SUB_ACCOUNT_LABEL.singular}</th>}
                  <th className="py-3.5 px-4">Assigned To</th>
                  <th className="py-3.5 px-4">{assetIdentifierLabel}</th>
                  <th className="py-3.5 px-4">{equipmentIdentifierLabel}</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4">Created</th>
                  {showActionsColumn && <th className="py-3.5 px-5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-content/5 text-xs">
                {serviceRequests.map((sr) => (
                  <tr key={sr.uid} className="hover:bg-base-200/40 transition-colors group">
                    {/* Request Number */}
                    <td className="py-3.5 px-5 font-bold">
                      <button
                        type="button"
                        onClick={() => navigate(`/service-requests/${sr.uid}`)}
                        className="text-primary hover:underline font-bold text-sm tracking-tight text-left"
                      >
                        {sr.requestNumber}
                      </button>
                    </td>

                    {/* Type */}
                    <td className="py-3.5 px-4">
                      <span className="font-semibold text-base-content/70">
                        {sr.typeLabel || sr.type}
                      </span>
                    </td>

                    {/* Customer Name */}
                    <td className="py-3.5 px-4">
                      <span className="font-bold text-base-content">{sr.customerName}</span>
                    </td>

                    {/* Account */}
                    {showAccountColumn && (
                      <td className="py-3.5 px-4 text-base-content/60 font-medium">
                        {sr.accountName || '—'}
                      </td>
                    )}

                    {/* Sub Account */}
                    {showSubAccountColumn && (
                      <td className="py-3.5 px-4 text-base-content/60 font-medium">
                        {sr.subAccountName || '—'}
                      </td>
                    )}

                    {/* Assigned To */}
                    <td className="py-3.5 px-4 text-base-content/60 font-medium">
                      {sr.assignedUserName || 'Unassigned'}
                    </td>

                    {/* Asset Identifier */}
                    <td className="py-3.5 px-4 text-base-content/50 font-mono">
                      {sr.assetIdentifier || '—'}
                    </td>

                    {/* Equipment Identifier */}
                    <td className="py-3.5 px-4 text-base-content/50 font-mono">
                      {sr.equipmentIdentifier || '—'}
                    </td>

                    {/* Status Badge */}
                    <td className="py-3.5 px-4 text-center">
                      <StatusBadge status={sr.status} />
                    </td>

                    {/* Created Date */}
                    <td className="py-3.5 px-4 text-base-content/70 font-medium whitespace-nowrap">
                      {sr.createdAt ? (
                        <div
                          className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                          data-tip={formatDateTime(sr.createdAt)}
                        >
                          <span className="cursor-default hover:text-base-content transition-colors">
                            {formatDate(sr.createdAt)}
                          </span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>

                    {/* Actions */}
                    {showActionsColumn && (
                      <td className="py-3.5 px-5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {can.service_requests.view() && (
                            <div
                              className={`tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap ${sr.hasActivePublicLink ? '' : 'cursor-not-allowed'}`}
                              data-tip={sr.hasActivePublicLink ? 'Copy Public Link' : 'No public link generated'}
                            >
                              <button
                                type="button"
                                onClick={() => handleCopyPublicLink(sr)}
                                disabled={!sr.hasActivePublicLink}
                                className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all disabled:opacity-30 disabled:pointer-events-none"
                                aria-label={sr.hasActivePublicLink ? 'Copy Public Link' : 'No public link generated'}
                              >
                                <Link2 size={15} />
                              </button>
                            </div>
                          )}
                          {can.service_requests.edit() && (
                            <div className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Edit">
                              <button
                                type="button"
                                onClick={() => navigate(`/service-requests/${sr.uid}/edit`)}
                                className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                aria-label={`Edit ${SERVICE_REQUEST_LABEL.singular}`}
                              >
                                <Edit2 size={15} />
                              </button>
                            </div>
                          )}
                          {can.service_requests.delete() && (
                            <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete">
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(sr)}
                                className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                aria-label={`Delete ${SERVICE_REQUEST_LABEL.singular}`}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Table Footer with Pagination */}
        {!loading && serviceRequests.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-base-content/60 font-medium">
              <span className="font-semibold text-base-content">
                {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-base-content">{pagination.total}</span> records
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-base-content/50 font-medium">
                  Page <span className="font-semibold text-base-content">{pagination.page}</span> /{' '}
                  <span className="font-semibold text-base-content">{pagination.totalPages}</span>
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pagination.page === 1 || loading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Previous Page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Next Page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={`Delete ${SERVICE_REQUEST_LABEL.singular}`}
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete {SERVICE_REQUEST_LABEL.singularLower}{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {deleteTarget?.requestNumber}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This action cannot be undone. All associated data will be removed.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : `Delete ${SERVICE_REQUEST_LABEL.singular}`}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ServiceRequests;

